import React, { useState, useEffect, useMemo } from 'react';
import { Stethoscope, Plus, X, Phone, Users, UserCheck, Search, Edit, Trash2, Filter, Printer, Mail, MapPin, Activity, Award, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';

// ==========================================
// 🎨 SUB-COMPONENT: PRESCRIBER CARD
// ==========================================
const PrescriberCard = ({ doc, onEdit, onDelete, onExpand, isExpanded, navigate }) => {
    const patientList = useMemo(() => {
        const str = doc.patients_handled;
        if (!str || typeof str !== 'string') return [];
        if (!str.includes('***')) return str.split(',').map(name => ({ name: name.trim(), id: null }));
        return str.split('||').map(p => {
            const parts = p.split('***');
            return { name: parts[0] ? parts[0].trim() : "Unknown", id: parts[1] ? parts[1].trim() : null };
        });
    }, [doc.patients_handled]);

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col relative group overflow-hidden h-full">
            {/* Premium Gradient Top Border */}
            <div className="h-1.5 w-full bg-gradient-to-r from-teal-400 to-blue-500 opacity-80" />

            {/* Action Overlay */}
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button onClick={() => onEdit(doc)} className="text-gray-400 hover:text-teal-600 bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow-sm border border-gray-100 transition-colors">
                    <Edit size={16} />
                </button>
                <button onClick={() => onDelete(doc.id)} className="text-gray-400 hover:text-red-500 bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow-sm border border-gray-100 transition-colors">
                    <Trash2 size={16} />
                </button>
            </div>

            <div className="p-6 flex-grow">
                {/* Header Section */}
                <div className="flex items-start gap-4 mb-5">
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-3 rounded-2xl text-teal-600 flex-shrink-0 shadow-sm border border-teal-200/50">
                        <Stethoscope size={24} />
                    </div>
                    <div className="min-w-0 pt-1">
                        <h3 className="font-bold text-lg text-gray-900 leading-tight group-hover:text-teal-700 transition-colors mb-1">
                            {doc.prescriber_name || "Unnamed Prescriber"}
                        </h3>
                        {doc.category && (
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-600 bg-teal-50/50 px-2 py-0.5 rounded-md border border-teal-100 w-fit">
                                <Briefcase size={10} /> {doc.category}
                            </div>
                        )}
                    </div>
                </div>

                {/* Contact Details Grid */}
                <div className="grid grid-cols-1 gap-3.5 mb-6">
                    {doc.prescriber_phone && (
                        <div className="flex items-center gap-3 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 flex-shrink-0">
                                <Phone size={14} />
                            </div>
                            <span className="font-medium">{doc.prescriber_phone}</span>
                        </div>
                    )}

                    {doc.fax && (
                        <div className="flex items-center gap-3 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 flex-shrink-0">
                                <Printer size={14} />
                            </div>
                            <span>{doc.fax}</span>
                        </div>
                    )}

                    {doc.email && (
                        <div className="flex items-center gap-3 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-400 flex-shrink-0">
                                <Mail size={14} />
                            </div>
                            <span className="truncate">{doc.email}</span>
                        </div>
                    )}

                    {doc.address && (
                        <div className="flex items-start gap-3 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-green-500 flex-shrink-0 mt-0.5">
                                <MapPin size={14} />
                            </div>
                            <span className="text-xs leading-relaxed line-clamp-2">{doc.address}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Expanded Patients Section */}
            <div className="mt-auto px-4 pb-4">
                <div className={`rounded-xl border border-gray-100 transition-all duration-300 ${isExpanded ? 'bg-white shadow-inner p-4' : 'bg-gray-50/50 p-3'}`}>
                    <div className="flex justify-between items-center mb-3">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                            <Activity size={12} className="text-teal-500" /> Patients Managed
                        </p>
                        {doc.patient_count > 0 && (
                            <span className="text-[10px] font-black text-white bg-teal-500 px-2 py-0.5 rounded-full shadow-sm">
                                {doc.patient_count}
                            </span>
                        )}
                    </div>

                    {patientList.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                            {(isExpanded ? patientList : patientList.slice(0, 3)).map((p, i) => (
                                <button
                                    key={i}
                                    onClick={() => p.id && navigate(`/patient/${p.id}`)}
                                    className={`
                                text-[10px] px-2.5 py-1 rounded-lg border flex items-center gap-1.5 transition-all
                                ${p.id
                                            ? 'bg-white border-gray-200 text-gray-700 hover:border-teal-400 hover:text-teal-700 hover:shadow-sm'
                                            : 'bg-gray-100 border-gray-100 text-gray-400 cursor-default'}
                            `}
                                >
                                    <UserCheck size={10} className={p.id ? 'text-teal-500' : 'text-gray-300'} /> {p.name}
                                </button>
                            ))}

                            {patientList.length > 3 && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onExpand(doc.id); }}
                                    className={`text-[10px] font-bold px-2.5 py-1 rounded-lg transition-colors 
                                ${isExpanded ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-teal-50 text-teal-600 hover:bg-teal-100'}`}
                                >
                                    {isExpanded ? 'Show Less' : `+${patientList.length - 3} More`}
                                </button>
                            )}
                        </div>
                    ) : (
                        <p className="text-[11px] text-gray-400 italic font-medium">No assigned patients found.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==========================================
// 📊 SUB-COMPONENT: TOP DASHBOARD
// ==========================================
const StatsDashboard = ({ totalDocs, totalPatients, categoriesCount }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-br from-white to-teal-50/30 p-5 rounded-2xl border border-teal-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-shadow">
            <div className="bg-teal-500 text-white p-3 rounded-xl shadow-lg ring-4 ring-teal-50 group-hover:scale-110 transition-transform">
                <Stethoscope size={24} />
            </div>
            <div>
                <p className="text-[11px] font-bold text-teal-600 uppercase tracking-wider mb-0.5">Total Prescribers</p>
                <h3 className="text-2xl font-black text-gray-800">{totalDocs}</h3>
            </div>
        </div>

        <div className="bg-gradient-to-br from-white to-blue-50/30 p-5 rounded-2xl border border-blue-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-shadow">
            <div className="bg-blue-500 text-white p-3 rounded-xl shadow-lg ring-4 ring-blue-50 group-hover:scale-110 transition-transform">
                <Users size={24} />
            </div>
            <div>
                <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-0.5">Active Patients</p>
                <h3 className="text-2xl font-black text-gray-800">{totalPatients}</h3>
            </div>
        </div>

        <div className="bg-gradient-to-br from-white to-purple-50/30 p-5 rounded-2xl border border-purple-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-shadow">
            <div className="bg-purple-500 text-white p-3 rounded-xl shadow-lg ring-4 ring-purple-50 group-hover:scale-110 transition-transform">
                <Award size={24} />
            </div>
            <div>
                <p className="text-[11px] font-bold text-purple-600 uppercase tracking-wider mb-0.5">Specializations</p>
                <h3 className="text-2xl font-black text-gray-800">{categoriesCount}</h3>
            </div>
        </div>
    </div>
);

// ==========================================
// 🚀 MAIN PAGE COMPONENT
// ==========================================
const Doctors = () => {
    const navigate = useNavigate();
    const [prescribers, setPrescribers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [expandedDocId, setExpandedDocId] = useState(null);
    const [selectedLetter, setSelectedLetter] = useState(null);
    const [form, setForm] = useState({ id: null, prescriber_name: '', prescriber_phone: '', fax: '', email: '', address: '', category: '' });
    const [isEditing, setIsEditing] = useState(false);

    const CATEGORIES = ["Allergy & Immunology", "Cardiology", "Dermatology", "Emergency Medicine", "Endocrinology", "Family Medicine", "Gastroenterology", "Internal Medicine", "Neurology", "Obstetrics & Gynecology", "Ophthalmology", "Orthopedic Surgery", "Pediatrics", "Psychiatry", "Pulmonology", "Radiology", "Surgery", "Urology"].sort();

    const fetchPrescribers = async () => {
        try {
            const res = await axios.get(`${API_URL}?action=get_doctors&_t=${Date.now()}`);
            if (Array.isArray(res.data)) setPrescribers(res.data);
            setLoading(false);
        } catch (error) { console.error(error); setLoading(false); }
    };

    useEffect(() => { fetchPrescribers(); }, []);

    const stats = useMemo(() => ({
        totalDocs: prescribers.length,
        totalPatients: prescribers.reduce((acc, curr) => acc + (curr.patient_count || 0), 0),
        uniqueCategories: [...new Set(prescribers.map(p => p.category).filter(c => c))].length
    }), [prescribers]);

    const filteredPrescribers = useMemo(() => {
        return prescribers
            .filter(doc => {
                const sTerm = searchTerm.toLowerCase();
                const cleanSearch = sTerm.replace(/\D/g, '');

                const nameMatch = (sTerm.length >= 1 && sTerm.length <= 2) 
                    ? (doc.prescriber_name || '').toLowerCase().startsWith(sTerm)
                    : (doc.prescriber_name || '').toLowerCase().includes(sTerm);
                const phoneMatch = (doc.prescriber_phone || '').replace(/\D/g, '').includes(cleanSearch);
                const rxMatch = (doc.rx_numbers || '').toLowerCase().includes(sTerm);

                const catMatch = !selectedCategory || doc.category === selectedCategory;
                const letterMatch = !selectedLetter || (doc.prescriber_name || '').toUpperCase().startsWith(selectedLetter);

                return (nameMatch || (cleanSearch && (phoneMatch || rxMatch)) || rxMatch) && catMatch && letterMatch;
            })
            .sort((a, b) => (a.prescriber_name || '').localeCompare(b.prescriber_name || ''));
    }, [prescribers, searchTerm, selectedCategory, selectedLetter]);

    const handleDelete = async (id) => {
        if (!window.confirm("Permanently remove this prescriber from the directory?")) return;
        try {
            await axios.post(`${API_URL}?action=delete_doctor`, { id });
            fetchPrescribers();
        } catch (error) { alert("Error deleting record"); }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}?action=${isEditing ? 'update_doctor' : 'add_doctor'}`, form);
            setShowModal(false);
            fetchPrescribers();
        } catch (error) { alert("Error saving record"); }
    };

    return (
        <div className="max-w-[1400px] mx-auto p-6 min-h-screen bg-gray-50/30">
            {/* HEADER SECTION */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h2 className="text-3xl font-black text-gray-900 flex items-center gap-3 tracking-tight">
                        <div className="bg-teal-600 p-2 rounded-xl text-white shadow-lg shadow-teal-200">
                            <Stethoscope size={28} />
                        </div>
                        Prescriber Directory
                    </h2>
                    <p className="text-gray-500 mt-2 text-sm font-medium">Manage medical professionals and track patient history.</p>
                </div>

                <div className="flex flex-wrap gap-3 items-center w-full md:w-auto">
                    <div className="relative flex-grow">
                        <Filter className="absolute left-3.5 top-3 text-gray-400" size={18} />
                        <select
                            className="pl-11 pr-8 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none w-full md:w-48 text-sm shadow-sm appearance-none cursor-pointer"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="">All Specialties</option>
                            {CATEGORIES.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
                        </select>
                    </div>

                    <div className="relative flex-grow">
                        <Search className="absolute left-3.5 top-3 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search Directory..."
                            className="pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none w-full md:w-64 text-sm shadow-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <button onClick={() => { setForm({ id: null, prescriber_name: '', prescriber_phone: '', fax: '', email: '', address: '', category: '' }); setIsEditing(false); setShowModal(true); }} className="bg-gray-900 text-white px-6 py-3 rounded-xl hover:bg-teal-700 hover:shadow-lg transition-all flex items-center gap-2 text-sm font-bold shadow-md">
                        <Plus size={18} /> Add New
                    </button>
                </div>
            </div>

            <StatsDashboard totalDocs={stats.totalDocs} totalPatients={stats.totalPatients} categoriesCount={stats.uniqueCategories} />

            {/* ALPHA INDEX BAR */}
            <div className="flex flex-wrap items-center gap-1.5 mb-8 bg-white p-2.5 rounded-2xl border border-gray-100 shadow-sm overflow-x-auto no-scrollbar">
                <button
                    onClick={() => setSelectedLetter(null)}
                    className={`px-4 py-1.5 rounded-xl text-xs font-black transition-all ${!selectedLetter ? 'bg-teal-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-50'}`}
                >
                    ALL
                </button>
                {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map(letter => (
                    <button
                        key={letter}
                        onClick={() => setSelectedLetter(selectedLetter === letter ? null : letter)}
                        className={`w-8 h-8 flex items-center justify-center rounded-xl text-xs font-black transition-all 
              ${selectedLetter === letter ? 'bg-teal-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-50 hover:text-teal-600'}`}
                    >
                        {letter}
                    </button>
                ))}
            </div>

            {/* DIRECTORY GRID */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 grayscale opacity-50">
                    <Activity className="animate-pulse text-teal-600 mb-4" size={48} />
                    <p className="text-lg font-bold text-gray-400">Loading Prescriber Directory...</p>
                </div>
            ) : filteredPrescribers.length === 0 ? (
                <div className="bg-white rounded-3xl p-20 text-center border-2 border-dashed border-gray-100">
                    <Search className="mx-auto text-gray-200 mb-6" size={80} />
                    <h3 className="text-2xl font-bold text-gray-800">No prescribers found</h3>
                    <p className="text-gray-500 mt-2">Try adjusting your filters or adding a new record.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    {filteredPrescribers.map(doc => (
                        <PrescriberCard
                            key={doc.id}
                            doc={doc}
                            onEdit={(d) => { setForm(d); setIsEditing(true); setShowModal(true); }}
                            onDelete={handleDelete}
                            onExpand={(id) => setExpandedDocId(expandedDocId === id ? null : id)}
                            isExpanded={expandedDocId === doc.id}
                            navigate={navigate}
                        />
                    ))}
                </div>
            )}

            {/* MODAL SECTION */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[550px] p-8 relative overflow-hidden">
                        {/* Modal Decoration */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-teal-50 rounded-full -mr-16 -mt-16 z-0" />

                        <div className="relative z-10">
                            <button onClick={() => setShowModal(false)} className="absolute -top-2 -right-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"><X size={24} /></button>
                            <div className="flex items-center gap-3 mb-8">
                                <div className="bg-teal-600 text-white p-2.5 rounded-xl shadow-lg shadow-teal-100">
                                    {isEditing ? <Edit size={20} /> : <Plus size={20} />}
                                </div>
                                <h3 className="text-2xl font-black text-gray-900">{isEditing ? 'Update Profile' : 'New Professional'}</h3>
                            </div>

                            <form onSubmit={handleSave} className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div className="col-span-2">
                                        <label className="block text-[11px] font-black text-teal-600 uppercase tracking-widest mb-2">Prescriber's Full Name</label>
                                        <input type="text" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 focus:bg-white outline-none transition-all font-medium" placeholder="e.g. Dr. John Smith" required value={form.prescriber_name} onChange={e => setForm({ ...form, prescriber_name: e.target.value })} />
                                    </div>

                                    <div className="col-span-2">
                                        <label className="block text-[11px] font-black text-blue-600 uppercase tracking-widest mb-2">Specialization / Category</label>
                                        <select className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none appearance-none cursor-pointer font-medium" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                            <option value="">Select Specialty</option>
                                            {CATEGORIES.map((cat, i) => <option key={i} value={cat}>{cat}</option>)}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Phone Number</label>
                                        <input type="text" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 focus:bg-white outline-none transition-all text-sm font-medium" placeholder="(555) 000-0000" value={form.prescriber_phone} onChange={e => setForm({ ...form, prescriber_phone: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-black text-gray-400 uppercase tracking-widest mb-2">Fax Number</label>
                                        <input type="text" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-teal-500 focus:bg-white outline-none transition-all text-sm font-medium" placeholder="(555) 111-1111" value={form.fax} onChange={e => setForm({ ...form, fax: e.target.value })} />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[11px] font-black text-red-400 uppercase tracking-widest mb-2">Email Address</label>
                                    <input type="email" className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-red-500 focus:bg-white outline-none transition-all text-sm font-medium" placeholder="doctor@clinic.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                                </div>

                                <div>
                                    <label className="block text-[11px] font-black text-green-500 uppercase tracking-widest mb-2">Office Practice Address</label>
                                    <textarea className="w-full bg-gray-50 border border-gray-200 p-3.5 rounded-xl focus:ring-2 focus:ring-green-500 focus:bg-white outline-none transition-all h-24 resize-none text-sm font-medium leading-relaxed" placeholder="123 Medical Dr, City, ST" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}></textarea>
                                </div>

                                <div className="pt-4 flex gap-3">
                                    <button type="button" onClick={() => setShowModal(false)} className="flex-grow bg-gray-100 text-gray-600 font-bold py-4 rounded-2xl hover:bg-gray-200 transition-colors">Cancel</button>
                                    <button type="submit" className="flex-[2] bg-gray-900 text-white font-bold py-4 rounded-2xl hover:bg-teal-600 shadow-xl transition-all">
                                        {isEditing ? 'Update Prescriber' : 'Register Prescriber'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Doctors;